import { create } from 'zustand';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { replayOutboxJob } from '@shared/posting/outboxStatus';
import { systemHealthService } from '../services/systemHealthService';
import type {
  DeadLetterJob,
  DocScanHealth,
  HttpTrends,
  NetSuiteTrends,
  OutboxLiveStats,
  OutboxTrends,
  TimeRange,
} from '../types/systemHealth.types';

interface SystemHealthState {
  range: TimeRange;

  // Live tiles — polled independently (5–10s). Kept in its own slice so its high-frequency updates
  // re-render only the tiles (fine-grained selectors), never the heavy trend charts.
  liveStats: OutboxLiveStats | null;

  // Document Capture health (86d3rrfee) — polled on the live cadence, own slice for the same reason.
  docScanHealth: DocScanHealth | null;

  // Range-driven trends — fetched on load, Refresh, and range change.
  outboxTrends: OutboxTrends | null;
  netsuiteTrends: NetSuiteTrends | null;
  httpTrends: HttpTrends | null;
  deadLetters: DeadLetterJob[];

  isLoadingTrends: boolean;
  trendsError: string | null;
  lastRefresh: string | null;

  setRange: (range: TimeRange) => Promise<void>;
  fetchTrends: () => Promise<void>;
  fetchLive: () => Promise<void>;
  fetchDocScanHealth: () => Promise<void>;
  refresh: () => Promise<void>;
  requeue: (jobId: number) => Promise<void>;
  clearError: () => void;
}

// 86d3r6gbq — request-sequence guard: only the newest fetchTrends may apply its result, so a slow response
// from an earlier range switch can't overwrite a newer range's data. Module-scoped (the store is a singleton).
let latestTrendsReq = 0;

export const useSystemHealthStore = create<SystemHealthState>()((set, get) => ({
  range: '30d',
  liveStats: null,
  docScanHealth: null,
  outboxTrends: null,
  netsuiteTrends: null,
  httpTrends: null,
  deadLetters: [],
  isLoadingTrends: false,
  trendsError: null,
  lastRefresh: null,

  setRange: async (range) => {
    set({ range });
    await get().fetchTrends();
  },

  fetchTrends: async () => {
    const mySeq = ++latestTrendsReq;
    const { range } = get();
    set({ isLoadingTrends: true, trendsError: null });
    try {
      const [outboxTrends, netsuiteTrends, httpTrends, deadLetters] = await Promise.all([
        systemHealthService.getOutboxTrends(range),
        systemHealthService.getNetSuiteTrends(range),
        systemHealthService.getHttpTrends(range),
        systemHealthService.getDeadLetters(),
      ]);
      // A newer range switch superseded this in-flight batch — drop its (now wrong-range) result.
      if (mySeq !== latestTrendsReq) return;
      set({
        outboxTrends,
        netsuiteTrends,
        httpTrends,
        deadLetters,
        isLoadingTrends: false,
        lastRefresh: new Date().toISOString(),
      });
    } catch (error) {
      // Stale failure from a superseded batch — the newer fetch owns the loading/error outcome.
      if (mySeq !== latestTrendsReq) return;
      set({ trendsError: getApiErrorMessage(error, 'Failed to load system health data'), isLoadingTrends: false });
    }
  },

  // Live tiles poll — deliberately does NOT flip isLoadingTrends (no skeleton flash every few seconds) and
  // swallows transient errors (the next tick self-heals; the trends error surface owns hard failures).
  fetchLive: async () => {
    try {
      const liveStats = await systemHealthService.getOutboxLive();
      set({ liveStats });
    } catch {
      /* transient — next poll retries */
    }
  },

  // Document Capture health poll — like fetchLive, no skeleton flash + swallows transient errors.
  fetchDocScanHealth: async () => {
    try {
      const docScanHealth = await systemHealthService.getDocumentCaptureHealth();
      set({ docScanHealth });
    } catch {
      /* transient — next poll retries */
    }
  },

  refresh: async () => {
    await Promise.all([get().fetchTrends(), get().fetchLive(), get().fetchDocScanHealth()]);
  },

  requeue: async (jobId) => {
    await replayOutboxJob(jobId);
    // Re-armed jobs are Pending, no longer dead-lettered — drop the row optimistically; the next live
    // poll reflects the new backlog. (The table also shows a "Requeued" badge before this resolves.)
    set({ deadLetters: get().deadLetters.filter((j) => j.id !== jobId) });
  },

  clearError: () => set({ trendsError: null }),
}));
